import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAvailableSurveys } from '@features/surveys/api';
import { useAccessibleTiles, useAccessibleCategories } from '@shared/lib/tilePermissions';
import type { CompiledMoreTileCategory, CompiledPublicTile } from '@shared/types/domainConfig';

/**
 * Which tiles a member should actually see, composing every visibility rule in
 * one place so the home rail, the "View all" sheet and the More page agree:
 *
 *   1. the permission gate (`tilePermissions`), then
 *   2. eForm availability — a `flow_template` tile whose survey is unpublished,
 *      past its deadline, or already submitted under a one-per-member policy
 *      opens a form the resident cannot submit, so it comes down entirely.
 *
 * Availability is whatever `/survey-responses/available` returns: the endpoint
 * already applies exactly those three rules per member, so membership of that
 * list IS "this eForm is still open for you".
 *
 * Fails OPEN. Until the list is known — loading, offline, a 403 — every eForm
 * tile stays put. A tile that lingers a moment too long beats tiles that blink
 * out whenever the network hiccups.
 */

/** The template a `flow_template` tile points at, or null for any other tile. */
function eformTemplateId(tile: CompiledPublicTile): string | null {
  if (tile.action_type !== 'flow_template') return null;
  return tile.action_config.survey_template_id ?? null;
}

export function hasEformTile(tiles: CompiledPublicTile[]): boolean {
  return tiles.some((tile) => eformTemplateId(tile) !== null);
}

/**
 * Drop eForm tiles that are no longer open. `openTemplateIds` of null means
 * "not known yet" and keeps every tile.
 */
export function filterOpenEformTiles(
  tiles: CompiledPublicTile[],
  openTemplateIds: Set<string> | null,
): CompiledPublicTile[] {
  if (!openTemplateIds) return tiles;

  return tiles.filter((tile) => {
    const templateId = eformTemplateId(tile);
    return templateId === null || openTemplateIds.has(templateId);
  });
}

/**
 * Template ids the member can still submit — null until known. Only queried
 * when an eForm tile is actually on screen, so members without one never pay
 * for the call.
 */
function useOpenEformIds(tiles: CompiledPublicTile[]): Set<string> | null {
  const enabled = hasEformTile(tiles);
  const query = useQuery({
    // Shared with SurveysList, so opening the list reuses this fetch.
    queryKey: ['surveys-available'],
    queryFn: fetchAvailableSurveys,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  return useMemo(() => {
    if (!enabled || !query.data) return null;

    return new Set(query.data.map((survey) => survey.survey_template_id));
  }, [enabled, query.data]);
}

export function useVisibleTiles(tiles: CompiledPublicTile[]): CompiledPublicTile[] {
  const permitted = useAccessibleTiles(tiles);
  const openEformIds = useOpenEformIds(permitted);
  return useMemo(() => filterOpenEformTiles(permitted, openEformIds), [permitted, openEformIds]);
}

export function useVisibleCategories(
  categories: CompiledMoreTileCategory[],
): CompiledMoreTileCategory[] {
  const permitted = useAccessibleCategories(categories);
  const allTiles = useMemo(() => permitted.flatMap((category) => category.tiles), [permitted]);
  const openEformIds = useOpenEformIds(allTiles);

  return useMemo(
    () =>
      permitted
        .map((category) => ({
          ...category,
          tiles: filterOpenEformTiles(category.tiles, openEformIds),
        }))
        .filter((category) => category.tiles.length > 0),
    [permitted, openEformIds],
  );
}
